import {
    createDisposer,
    createLogPrefix,
    type Disposable,
    getUserAgentInfo,
    logDebug,
    logWarn,
    type LogTarget,
    requestWithRetry,
} from '@amazon/vinyl-util'
import type {
    LifecycleSource,
    PlaybackAuth,
    PlaybackDeviceProfile,
    PlaybackEndpoints,
    PlaybackViewport,
} from '../env/PlaybackEnv'
import {
    type MetrixAction,
    type MetrixActionQueue,
    toAdditionalDetails,
    type WireDetails,
} from './metrixAction'

// The MusicMetrix (AMTC) uploader every metric family shares: batches each action
// onto the wire contract GH's `@amzn/music-metrix-sdk-typescript` `HttpReporter`
// reports, then POSTs it. Emission is observational: `enqueue` never throws, and
// a failed upload is logged and dropped.

// Metrix SDK `amtcEventsEndpoint`; amp targets the `.com` host, so no `/ZAZ` prefix.
const METRIX_INGESTION_PATH = '/telemetry/v1/events'

const DEFAULT_BATCH_SIZE = 20

// GH's value until the metrics owners settle amp's own (Blackbolt's Cradle profiles count only BLACKBOLT).
export const METRIX_PRODUCT_VERSION = 'GREENHORNET'

const DEVICE_LOCALE = /^[a-z]{2}-[A-Z]{2}$/

// The platform locale, as the SDK's LocaleCollector reads it; the app's display language is `appLocale`.
function deviceLocale(language: string): string {
    return DEVICE_LOCALE.test(language) ? language.replace('-', '_') : 'en_US'
}

function deviceProperties(viewport: PlaybackViewport | undefined): string | undefined {
    if (!viewport) return undefined
    const { width, height } = viewport
    return JSON.stringify({
        displayWidthPixels: width,
        displayHeightPixels: height,
        viewType: width > height ? 'LANDSCAPE' : 'PORTRAIT',
    })
}

/**
 * What GH's Metrix SDK stamps on every action of every family: its `globalAttributes`
 * and the web collectors' `locale`, `userAgent` and `deviceProperties`. They override
 * the action's own, as the SDK's merge does.
 */
function metrixGlobalDetails(env: MetrixUploaderEnv): WireDetails {
    const { locale, appVersion, viewport } = env.device
    return {
        locale: deviceLocale(locale),
        productVersion: METRIX_PRODUCT_VERSION,
        productVersionDetail: appVersion,
        userAgent: getUserAgentInfo().userAgent || undefined,
        deviceProperties: deviceProperties(viewport),
    }
}

/** One row of the AMTC `clientActionList` (the SDK's `ClientAction`). */
interface ClientAction extends MetrixAction {
    readonly contentInfo: readonly Record<string, string>[]
}

/**
 * The AMTC ingestion payload — a SINGLE object per batch (the SDK's
 * `FormattedMetrixPayload`), NOT an array. Identity fields are top-level and the
 * per-event rows live under `clientActionList`.
 */
interface FormattedMetrixPayload {
    readonly customerId?: string
    readonly deviceId: string
    readonly deviceType: string
    readonly clientActionList: readonly ClientAction[]
}

interface RequestAuth {
    readonly headers: Record<string, string>
    readonly credentials?: RequestCredentials
    readonly customerId?: string
    readonly deviceId: string
    readonly deviceType: string
}

/** What an uploader reads from its host; a whole `PlaybackEnv` is one. */
export interface MetrixUploaderEnv {
    readonly auth: Pick<
        PlaybackAuth,
        'usesBearer' | 'requestCredentials' | 'getCsrfHeaders' | 'getCustomerId'
    >
    readonly device: Pick<
        PlaybackDeviceProfile,
        'deviceId' | 'deviceTypeId' | 'appVersion' | 'locale' | 'viewport'
    >
    readonly endpoints: Pick<PlaybackEndpoints, 'apiHost' | 'pageUrl'>
    readonly lifecycle: LifecycleSource
}

export class MetrixUploader implements MetrixActionQueue, LogTarget, Disposable {
    logPrefix = createLogPrefix('MetrixUploader')

    private readonly buffer: MetrixAction[] = []
    private readonly disposer = createDisposer()
    private pageHidden = false

    constructor(
        private readonly env: MetrixUploaderEnv,
        private readonly batchSize: number = DEFAULT_BATCH_SIZE
    ) {
        const { lifecycle } = env
        // Last chance to ship a sub-threshold buffer. Both signals are needed:
        // terminate covers unload/bfcache, hidden covers tab-switch.
        this.disposer.add(
            lifecycle.onTerminate(() => {
                this.pageHidden = true
                this.flush({ keepalive: true })
            })
        )
        this.disposer.add(
            lifecycle.onResume(() => {
                this.pageHidden = false
            })
        )
        this.disposer.add(
            lifecycle.hidden$.onData((hidden) => {
                if (hidden) this.flush({ keepalive: true })
            })
        )
    }

    enqueue(action: MetrixAction): void {
        this.buffer.push({
            ...action,
            additionalDetails: {
                ...action.additionalDetails,
                ...toAdditionalDetails(metrixGlobalDetails(this.env)),
            },
        })
        // Past the page-hide flush nothing else would ship a late event, e.g. an unload's systemStop.
        if (this.pageHidden) this.flush({ keepalive: true })
        else if (this.buffer.length >= this.batchSize) this.flush()
    }

    flush(options?: { readonly keepalive?: boolean }): void {
        if (this.buffer.length === 0) return
        const batch = this.buffer.splice(0, this.buffer.length)
        // Never retried past requestWithRetry's own retry.
        this.upload(batch, options?.keepalive ?? false).catch((error) =>
            logWarn(this, 'upload failed', error instanceof Error ? error.message : String(error))
        )
    }

    dispose(): void {
        this.flush({ keepalive: true })
        this.disposer.dispose()
    }

    private async upload(batch: readonly MetrixAction[], keepalive: boolean): Promise<void> {
        if (this.env.auth.usesBearer()) {
            logDebug(this, 'skip upload (bearer auth cannot authenticate AMTC — would 403)', {
                count: batch.length,
            })
            return
        }
        const auth = buildRequestAuth(this.env)
        const payload: FormattedMetrixPayload = {
            ...(auth.customerId ? { customerId: auth.customerId } : null),
            deviceId: auth.deviceId,
            deviceType: auth.deviceType,
            clientActionList: batch.map((action) => ({
                ...action,
                contentInfo: action.contentInfo ?? [],
            })),
        }
        logDebug(this, 'upload', { count: batch.length, keepalive })
        await requestWithRetry(`${this.env.endpoints.apiHost()}${METRIX_INGESTION_PATH}`, {
            method: 'POST',
            headers: auth.headers,
            ...(auth.credentials ? { credentials: auth.credentials } : null),
            // keepalive lets an in-flight page-hide POST outlive the document.
            ...(keepalive ? { keepalive: true } : null),
            body: JSON.stringify(payload),
        })
    }
}

/**
 * Assemble the ingestion request's auth headers + identity from the `Auth`
 * capability, as `gql.ts` / `relayAuthHeaders.ts` do — no hand-rolled crypto or
 * SigV4. Only the cookie-session path is built (the uploader gates bearer auth out
 * first): no `x-amz-access-token`, the CSRF triple + device headers ride along, and
 * `credentials: 'include'` carries the first-party session cookie. Auth/csrf stay
 * OUT of the POST body — putting them there is how GH's GREENH-332 leaked.
 */
function buildRequestAuth({ auth, device, endpoints }: MetrixUploaderEnv): RequestAuth {
    const { deviceId, deviceTypeId } = device

    const headers: Record<string, string> = {
        'content-type': 'application/json',
        // AMTC ingestion reads `x-amz-*` device headers (the Metrix SDK's
        // `AuthHeaders`), NOT the `x-amzn-*` spelling Firefly `gql.ts` sends.
        'x-amz-device-id': deviceId,
        'x-amz-device-type': deviceTypeId,
        ...auth.getCsrfHeaders(),
    }

    const customerId = auth.getCustomerId() ?? undefined
    if (customerId) headers['x-amz-customer-id'] = customerId
    const pageUrl = endpoints.pageUrl()
    if (pageUrl) headers['x-amz-request-url'] = pageUrl

    const credentials = auth.requestCredentials
    return {
        headers,
        ...(credentials ? { credentials } : null),
        ...(customerId ? { customerId } : null),
        deviceId,
        deviceType: deviceTypeId,
    }
}
